/**
 * 認証関連のReact Query Hooks
 * TanStack Query v5 完全実装 with オプティミスティック更新 & エラーリトライ戦略
 */

import { 
  useQuery, 
  useMutation, 
  useQueryClient 
} from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { showNotification } from '@mantine/notifications';
// Note: Icons removed from hooks.ts as JSX cannot be used in .ts files
import { authApi, tokenManager } from '@/features/auth/api/index';
import type { 
  LoginFormData, 
  NFCLoginData, 
  User, 
  Staff,
  AuthError
} from '@/features/auth/types';
import type {
  HttpErrorDetails
} from '@/features/auth/types/api';

// クエリキーファクトリー
export const authKeys = {
  all: ['auth'] as const,
  currentUser: () => [...authKeys.all, 'currentUser'] as const,
  verifyToken: () => [...authKeys.all, 'verifyToken'] as const,
  session: () => [...authKeys.all, 'session'] as const,
} as const;

/**
 * 現在のユーザー情報を取得（最適化されたキャッシュ戦略）
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: authKeys.currentUser(),
    queryFn: authApi.getCurrentUser,
    enabled: tokenManager.hasToken(),
    
    // キャッシュ戦略
    staleTime: 10 * 60 * 1000, // 10分間フレッシュ
    gcTime: 30 * 60 * 1000,    // 30分間メモリ保持
    
    // リトライ戦略
    retry: (failureCount, error: unknown) => {
      // Type-safe error checking
      const httpError = error as HttpErrorDetails | undefined;
      // 認証エラー（401, 403）は再試行しない
      if (httpError?.status === 401 || httpError?.status === 403) {
        return false;
      }
      // ネットワークエラーのみ3回まで再試行
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    // UX最適化
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

/**
 * トークン検証（セッション管理強化）
 */
export const useVerifyToken = () => {
  return useQuery({
    queryKey: authKeys.verifyToken(),
    queryFn: authApi.verifyToken,
    enabled: tokenManager.hasToken(),
    
    // セッション管理に適したキャッシュ設定
    staleTime: 2 * 60 * 1000,  // 2分間フレッシュ（セキュリティ重視）
    gcTime: 5 * 60 * 1000,     // 5分間メモリ保持
    
    // トークン無効時は即座に失敗
    retry: false,
    
    // セキュリティ重視の再取得
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
};

/**
 * ログイン処理（オプティミスティック更新 & 強化されたエラーハンドリング）
 */
export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: LoginFormData) => authApi.login(data),
    
    // オプティミスティック更新
    onMutate: async () => {
      // 進行中のクエリをキャンセル
      await queryClient.cancelQueries({ queryKey: authKeys.all });
      
      // 楽観的状態を設定（ローディング状態のみ）
      queryClient.setQueryData(authKeys.verifyToken(), true);
      
      // ロールバック用の以前の状態を保存
      const previousUser = queryClient.getQueryData(authKeys.currentUser());
      const previousToken = queryClient.getQueryData(authKeys.verifyToken());
      
      return { previousUser, previousToken };
    },
    
    // 成功時の処理
    onSuccess: (response) => {
      // トークンを安全に保存
      if (response.data.token) {
        tokenManager.setToken(response.data.token);
        
        // 認証状態を更新
        queryClient.setQueryData(authKeys.verifyToken(), true);
      }

      // ユーザー情報をキャッシュに設定
      const user = response.data.user || response.data.staff;
      if (user) {
        queryClient.setQueryData(authKeys.currentUser(), user);
        
        // 関連キャッシュを無効化（フレッシュデータを取得）
        queryClient.invalidateQueries({ queryKey: authKeys.session() });
      }

      // 成功通知（シンプルで統一された通知）
      showNotification({
        title: 'ログイン成功',
        message: `${user?.name}さん、ようこそ`,
        color: 'green',
        autoClose: 3000,
      });

      // スムーズな画面遷移
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1000);
    },
    
    // エラー時の処理
    onError: (error: unknown, _variables, context) => {
      // オプティミスティック更新をロールバック
      if (context?.previousUser !== undefined) {
        queryClient.setQueryData(authKeys.currentUser(), context.previousUser);
      }
      if (context?.previousToken !== undefined) {
        queryClient.setQueryData(authKeys.verifyToken(), context.previousToken);
      }
      
      // エラー種別に応じた詳細な通知（型安全）
      let title = 'ログインエラー';
      let message = 'ログインに失敗しました';
      const color = 'red';
      
      // Type-safe error handling
      const httpError = error as HttpErrorDetails;
      const authError = error as AuthError;
      
      if (httpError.status === 401) {
        message = 'メールアドレスまたはパスワードが正しくありません';
      } else if (httpError.status === 429) {
        title = 'アクセス制限';
        message = 'ログイン試行回数が制限を超えました。しばらく待ってから再試行してください';
      } else if (httpError.status === 422) {
        message = httpError.response?.data?.message || '入力内容に問題があります';
      } else if (httpError.status && httpError.status >= 500) {
        title = 'サーバーエラー';
        message = 'サーバーに問題が発生しています。しばらく待ってから再試行してください';
      } else if (authError.message) {
        message = authError.message;
      }
      
      showNotification({
        title,
        message,
        color,
        autoClose: 5000,
      });
    },
    
    // リトライ戦略（ネットワークエラーのみ）
    retry: (failureCount, error: unknown) => {
      // Type-safe error checking
      const httpError = error as HttpErrorDetails | undefined;
      // クライアントエラー（4xx）は再試行しない
      if (httpError?.status && httpError.status >= 400 && httpError.status < 500) {
        return false;
      }
      // ネットワークエラーは2回まで再試行
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    
    // 最終的にキャッシュをクリーンアップ
    onSettled: () => {
      // 認証関連のクエリを再検証
      queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });
};

/**
 * NFCログイン処理（高速認証 & 包括的エラーハンドリング）
 * @param options - 設定オプション
 * @param options.autoNavigate - 自動画面遷移を行うかどうか（デフォルト: true）
 */
export const useNFCLogin = (options: { autoNavigate?: boolean } = {}) => {
  const { autoNavigate = true } = options;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: NFCLoginData) => authApi.nfcLogin(data),
    
    // NFCは高速処理が重要なのでオプティミスティック更新を積極活用
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: authKeys.all });
      
      // NFCタッチの即座の反応を提供
      queryClient.setQueryData(authKeys.verifyToken(), true);
      
      const previousUser = queryClient.getQueryData(authKeys.currentUser());
      const previousToken = queryClient.getQueryData(authKeys.verifyToken());
      
      return { previousUser, previousToken };
    },
    
    onSuccess: (response) => {
      // 高速トークン設定
      if (response.data.token) {
        tokenManager.setToken(response.data.token);
        queryClient.setQueryData(authKeys.verifyToken(), true);
      }

      // スタッフ情報を即座にキャッシュ
      const user = response.data.staff || response.data.user;
      if (user) {
        queryClient.setQueryData(authKeys.currentUser(), user);
        
        // バックグラウンドでセッション情報を更新
        queryClient.prefetchQuery({
          queryKey: authKeys.session(),
          queryFn: () => authApi.getCurrentUser(),
          staleTime: 5 * 60 * 1000,
        });
      }

      // NFCログイン専用の成功フィードバック（通知は条件付きで表示）
      if (autoNavigate) {
        // シンプルな成功通知一つのみ
        showNotification({
          title: 'ログイン成功',
          message: `${user?.name}さん、ようこそ`,
          color: 'green',
          autoClose: 3000,
        });

        // 高速画面遷移（autoNavigateがtrueの場合のみ）
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 500);
      }
      // autoNavigateがfalseの場合、通知も画面遷移も行わない
      // 呼び出し元のコンポーネントが制御する
    },
    
    onError: (error: unknown, _variables, context) => {
      // オプティミスティック更新のロールバック
      if (context?.previousUser !== undefined) {
        queryClient.setQueryData(authKeys.currentUser(), context.previousUser);
      }
      if (context?.previousToken !== undefined) {
        queryClient.setQueryData(authKeys.verifyToken(), context.previousToken);
      }
      
      // NFC特有のエラーハンドリング（型安全）
      let title = 'NFC認証エラー';
      let message = 'NFCログインに失敗しました';
      let color: 'red' | 'orange' | 'yellow' = 'red';
      
      // Type-safe error handling
      const httpError = error as HttpErrorDetails;
      const authError = error as AuthError;
      
      if (httpError.status === 422) {
        title = 'カード未登録';
        message = '登録されていないNFCカードです。管理者にお問い合わせください';
        color = 'orange';
      } else if (httpError.status === 403) {
        title = 'アクセス拒否';
        message = 'このカードは現在無効化されています';
        color = 'red';
      } else if (httpError.status === 429) {
        title = 'アクセス制限';
        message = 'NFCアクセス頻度が制限を超えました';
        color = 'yellow';
      } else if (authError.code === 'NETWORK_ERROR') {
        title = 'ネットワークエラー';
        message = 'ネットワーク接続を確認してください';
      } else if (httpError.response?.data?.message) {
        message = httpError.response.data.message;
      }
      
      showNotification({
        title,
        message,
        color,
        autoClose: 4000,
      });
    },
    
    // NFC用の積極的リトライ戦略
    retry: (failureCount, error: unknown) => {
      // Type-safe error checking
      const httpError = error as HttpErrorDetails | undefined;
      // カード未登録やアクセス拒否は再試行しない
      if (httpError?.status === 422 || httpError?.status === 403) {
        return false;
      }
      // ネットワークエラーは3回まで再試行（NFCは物理接触なので確実に処理したい）
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 5000), // NFC用の短いリトライ間隔
    
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });
};

/**
 * NFCログイン処理（ナビゲーション制御なし）
 * LoginPageなど、独自のタイミング制御を行うコンポーネント用
 */
export const useNFCLoginWithoutNavigation = () => {
  return useNFCLogin({ autoNavigate: false });
};

/**
 * ログアウト処理（確実なセッション終了 & クリーンアップ）
 */
export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: authApi.logout,
    
    // ログアウトのオプティミスティック更新（即座に認証状態をクリア）
    onMutate: async () => {
      // 即座にローカル認証状態をクリア
      queryClient.setQueryData(authKeys.currentUser(), null);
      queryClient.setQueryData(authKeys.verifyToken(), false);
      
      // ロールバック用の状態保存
      const previousUser = queryClient.getQueryData(authKeys.currentUser());
      const previousToken = queryClient.getQueryData(authKeys.verifyToken());
      
      return { previousUser, previousToken };
    },
    
    onSuccess: () => {
      // 完全なクリーンアップ
      tokenManager.removeToken();
      
      // すべての認証関連キャッシュを削除
      queryClient.removeQueries({ queryKey: authKeys.all });
      
      // その他のユーザー固有データもクリア
      queryClient.clear();
      
      // ログアウト成功通知
      showNotification({
        title: 'ログアウト完了',
        message: 'お疲れ様でした',
        color: 'blue',
        autoClose: 2000,
      });

      // 確実なページ遷移
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 500);
    },
    
    onError: () => {
      // エラーが発生してもローカルログアウトは実行
      tokenManager.removeToken();
      queryClient.clear();
      
      // エラー通知（サーバーログアウトに失敗した場合）
      showNotification({
        title: 'ログアウト',
        message: 'ローカルログアウトを実行しました',
        color: 'yellow',
        autoClose: 3000,
      });
      
      // 強制的にログイン画面へ
      navigate('/login', { replace: true });
    },
    
    // ログアウトは基本的に再試行しない（クライアント側処理重視）
    retry: false,
  });
};

/**
 * パスワードリセットリクエスト
 */
export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: (email: string) => authApi.requestPasswordReset(email),
    onSuccess: () => {
      showNotification({
        title: '送信完了',
        message: 'パスワードリセットメールを送信しました',
        color: 'green',
      });
    },
    onError: (error: unknown) => {
      const httpError = error as HttpErrorDetails;
      const authError = error as AuthError;
      const message = httpError.response?.data?.message || authError.message || 'メールの送信に失敗しました';
      showNotification({
        title: 'エラー',
        message,
        color: 'red',
      });
    },
  });
};

/**
 * パスワードリセット実行
 */
export const useResetPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: authApi.resetPassword,
    onSuccess: () => {
      showNotification({
        title: '完了',
        message: 'パスワードがリセットされました',
        color: 'green',
      });

      // ログイン画面へ遷移
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    },
    onError: (error: unknown) => {
      const httpError = error as HttpErrorDetails;
      const authError = error as AuthError;
      const message = httpError.response?.data?.message || authError.message || 'パスワードのリセットに失敗しました';
      showNotification({
        title: 'エラー',
        message,
        color: 'red',
      });
    },
  });
};

/**
 * 認証状態を管理するカスタムフック
 */
/**
 * 統合認証状態管理フック（型安全性強化）
 */
export interface AuthStateReturn {
  user: User | Staff | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginFormData) => void;
  nfcLogin: (data: NFCLoginData) => void;
  logout: () => void;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  error: AuthError | HttpErrorDetails | null;
}

export const useAuthState = (): AuthStateReturn => {
  const { data: user, isLoading, error: userError } = useCurrentUser();
  const { data: isValid, error: tokenError } = useVerifyToken();
  const loginMutation = useLogin();
  const nfcLoginMutation = useNFCLogin();
  const logoutMutation = useLogout();

  // Aggregate errors from all auth operations (type-safe)
  const error = (loginMutation.error || nfcLoginMutation.error || logoutMutation.error || userError || tokenError || null) as AuthError | HttpErrorDetails | null;

  return {
    user: user || null,
    isAuthenticated: !!user && !!isValid,
    isLoading,
    login: loginMutation.mutate,
    nfcLogin: nfcLoginMutation.mutate,
    logout: () => logoutMutation.mutate(),
    isLoggingIn: loginMutation.isPending || nfcLoginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    error,
  };
};